import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Building2, CalendarClock, ChevronLeft, HelpCircle } from 'lucide-react';
import { publicRdvProBookingApi, publicDocumentApi } from '../../lib/api';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';

interface Info { company_name: string; logo_url: string; theme_color: string; duration_minutes: number; professionals_only: boolean; location_mode: 'domicile' | 'entreprise' | 'les_deux' | null; known_address: { address: string; postal_code: string; city: string } | null; }
interface Choice { id: string; label: string; price_ttc: number; vat_rate: number; has_quantity: boolean; }
interface CustomQuestionOption { id: string; label: string; message: string; block: boolean; has_detail_field: boolean; }
interface CustomQuestion { id: string; question: string; type: 'text' | 'select' | 'datetime'; options: CustomQuestionOption[]; allow_multiple: boolean; required: boolean; help_text: string; help_image: string; allow_end_date?: boolean; }
interface Template {
    id: string; name: string; instructions_text: string;
    response_type: 'choice' | 'question' | 'both'; allow_multiple_choices: boolean;
    choices: Choice[]; custom_questions: CustomQuestion[]; rdv_duration_minutes: number | null;
}

const PoweredByFooter: React.FC = () => (
    <a href="https://gestion.l-iamani.com" target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);
const todayInput = () => new Date().toISOString().split('T')[0];
const getSelectedLabels = (answer?: string) => (answer ? answer.split(', ').filter(Boolean) : []);

/** Widget public de réservation en ligne (aucune authentification) pour RDV Pro (Restauration) — équivalent
 *  du widget /booking pour les clients Prestation, mais sans catalogue de prestations. Si au moins une
 *  "Démarche client" avec prise de RDV est configurée, le visiteur choisit d'abord le type de rendez-vous
 *  (chacune garde ses propres choix/questions personnalisées/durée) ; sinon, réservation directe classique. */
export const RdvProBookingWidget: React.FC = () => {
    // Le segment d'URL est soit l'identifiant lisible de l'établissement ("n-signature"), soit un ancien id
    // opaque (liens envoyés avant l'introduction de cette fonctionnalité) — résolu vers l'id réel du client
    // ci-dessous avant tout autre appel, cf. GET /public/resolve-establishment/:idOrSlug.
    const { clientId: idOrSlug } = useParams<{ clientId: string }>();
    // Lien envoyé depuis une fiche contact CRM existante (cf. bouton "Envoyer un lien de rendez-vous" côté
    // CRM) : le contact est déjà connu, on saute entièrement la saisie de ses coordonnées.
    const knownContactId = React.useMemo(() => new URLSearchParams(window.location.search).get('contact') || '', []);
    const [clientId, setClientId] = React.useState<string | null>(null);
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'type' | 'form' | 'success'>('loading');
    const [info, setInfo] = React.useState<Info | null>(null);
    const [templates, setTemplates] = React.useState<Template[]>([]);
    const [selectedTemplate, setSelectedTemplate] = React.useState<Template | null>(null);

    const [selections, setSelections] = React.useState<Record<string, number>>({});
    const [questionAnswers, setQuestionAnswers] = React.useState<Record<string, string>>({});
    const [optionDetails, setOptionDetails] = React.useState<Record<string, string>>({});
    const [questionDatetime, setQuestionDatetime] = React.useState<Record<string, { date_debut: string; heure_debut: string; date_fin: string; heure_fin: string }>>({});
    const [selectedDate, setSelectedDate] = React.useState(todayInput());
    const [slots, setSlots] = React.useState<string[]>([]);
    const [loadingSlots, setLoadingSlots] = React.useState(false);
    const [selectedSlot, setSelectedSlot] = React.useState('');

    const [locationType, setLocationType] = React.useState<'domicile' | 'entreprise'>('domicile');
    const [address, setAddress] = React.useState('');
    const [addressSelected, setAddressSelected] = React.useState(false);
    const [postalCode, setPostalCode] = React.useState('');
    const [city, setCity] = React.useState('');
    const [kmFee, setKmFee] = React.useState<{ fee: number; distance_km: number } | null>(null);
    const [contactType, setContactType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [companyName, setCompanyName] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [openHelpIds, setOpenHelpIds] = React.useState<Set<string>>(new Set());
    const toggleQuestionHelp = (id: string) => setOpenHelpIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    React.useEffect(() => {
        const root = document.documentElement;
        const previousTheme = root.getAttribute('data-theme');
        const wasDark = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        root.classList.add('light');
        return () => {
            if (previousTheme) root.setAttribute('data-theme', previousTheme); else root.removeAttribute('data-theme');
            root.classList.toggle('dark', wasDark);
            root.classList.toggle('light', !wasDark);
        };
    }, []);

    React.useEffect(() => {
        if (!idOrSlug) { setPhase('notfound'); return; }
        publicRdvProBookingApi.resolveEstablishment(idOrSlug)
            .then((data: any) => {
                if (!data?.client_id) { setPhase('notfound'); return; }
                setClientId(data.client_id);
            })
            .catch(() => setPhase('notfound'));
    }, [idOrSlug]);

    React.useEffect(() => {
        if (!clientId) return;
        Promise.all([publicRdvProBookingApi.getInfo(clientId, knownContactId), publicRdvProBookingApi.getTemplates(clientId)]).then(([data, tplRes]: any[]) => {
            if (!data || data.error) { setPhase('notfound'); return; }
            setInfo(data);
            if (data.professionals_only) setContactType('PROFESSIONNEL');
            // Adresse déjà connue du contact CRM (lien envoyé depuis une fiche existante) — préremplie pour
            // lui faire gagner du temps, avec le statut "sélectionnée" (fiable, vient de la fiche client).
            if (data.known_address?.address) {
                setAddress(data.known_address.address);
                setPostalCode(data.known_address.postal_code || '');
                setCity(data.known_address.city || '');
                setAddressSelected(true);
            }
            const tpls: Template[] = Array.isArray(tplRes?.templates) ? tplRes.templates : [];
            setTemplates(tpls);
            if (tpls.length === 1) { setSelectedTemplate(tpls[0]); setPhase('form'); }
            else if (tpls.length > 1) setPhase('type');
            else setPhase('form');

            const brandColor = data.theme_color || '#987b55';
            document.documentElement.style.setProperty('--brand-color', brandColor);
            const hex = brandColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            document.documentElement.style.setProperty('--brand-fg', luminance > 0.5 ? '#000000' : '#ffffff');
        }).catch(() => setPhase('notfound'));
    }, [clientId]);

    const [computedDuration, setComputedDuration] = React.useState<number | null>(null);
    React.useEffect(() => {
        if (phase !== 'form' || !clientId || !selectedDate) { setSlots([]); return; }
        setLoadingSlots(true);
        setSelectedSlot('');
        const choicesParam = selectedTemplate ? Object.entries(selections).map(([id, qty]) => ({ id, qty })) : undefined;
        publicRdvProBookingApi.getAvailability(clientId, selectedDate, selectedTemplate?.id, choicesParam).then((data: any) => {
            setSlots(Array.isArray(data?.slots) ? data.slots : []);
            if (data?.duration_minutes) setComputedDuration(data.duration_minutes);
        }).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
    }, [phase, clientId, selectedDate, selectedTemplate, selections]);

    const isDomicileRdv = info?.location_mode === 'les_deux' ? locationType === 'domicile' : info?.location_mode === 'domicile';
    const selectedChoices = selectedTemplate ? selectedTemplate.choices.filter((c) => c.id in selections) : [];
    const orderTotal = selectedChoices.reduce((sum, c) => sum + c.price_ttc * (selections[c.id] || 1), 0);

    // Estimation des frais kilométriques affichée en direct pendant la saisie de l'adresse — pour que le
    // client voie le vrai total (y compris les frais) avant de payer, pas seulement une fois sur Stripe/Revolut.
    React.useEffect(() => {
        if (!clientId || !isDomicileRdv || !address.trim()) { setKmFee(null); return; }
        const timer = setTimeout(() => {
            publicRdvProBookingApi.getKmFeeEstimate(clientId, address.trim(), postalCode.trim(), city.trim())
                .then((data: any) => setKmFee(data?.fee > 0 ? data : null))
                .catch(() => setKmFee(null));
        }, 600);
        return () => clearTimeout(timer);
    }, [clientId, isDomicileRdv, address, postalCode, city]);
    const isBlocked = selectedTemplate ? selectedTemplate.custom_questions.some((q) => q.type === 'select' &&
        getSelectedLabels(questionAnswers[q.id]).some((label) => q.options.find((o) => o.label === label)?.block)
    ) : false;

    const formatDateFr = (d: string) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };

    const buildAnswer = (q: CustomQuestion) => {
        if (q.type === 'datetime') {
            const dt = questionDatetime[q.id];
            if (!dt?.date_debut || !dt?.heure_debut) return '';
            let s = `${formatDateFr(dt.date_debut)} ${dt.heure_debut}`;
            if (dt.date_fin || dt.heure_fin) s += ` → ${dt.date_fin ? formatDateFr(dt.date_fin) : formatDateFr(dt.date_debut)}${dt.heure_fin ? ' ' + dt.heure_fin : ''}`;
            return s;
        }
        if (q.type !== 'select') return (questionAnswers[q.id] || '').trim();
        return getSelectedLabels(questionAnswers[q.id]).map((label) => {
            const opt = q.options.find((o) => o.label === label);
            if (opt?.has_detail_field) {
                const detail = (optionDetails[opt.id] || '').trim();
                return detail ? `${label} : ${detail}` : label;
            }
            return label;
        }).join(', ');
    };

    // Champs structurés (pas seulement le texte lisible ci-dessus) pour une question "Date et heure" — même
    // forme que côté DocumentationResponse.tsx, consommée par le même backend (POST .../respond).
    const buildDatetimePayload = (q: CustomQuestion) => {
        if (q.type !== 'datetime') return null;
        const dt = questionDatetime[q.id];
        if (!dt?.date_debut || !dt?.heure_debut) return null;
        const payload: Record<string, string> = { date_debut: dt.date_debut, heure_debut: dt.heure_debut };
        if (q.allow_end_date !== false) {
            if (dt.date_fin) payload.date_fin = dt.date_fin;
            if (dt.heure_fin) payload.heure_fin = dt.heure_fin;
        }
        return payload;
    };

    const toggleChoice = (choice: Choice) => {
        if (!selectedTemplate) return;
        setSelections((prev) => {
            const isSelected = choice.id in prev;
            if (selectedTemplate.allow_multiple_choices) {
                const next = { ...prev };
                if (isSelected) delete next[choice.id]; else next[choice.id] = 1;
                return next;
            }
            return isSelected ? {} : { [choice.id]: 1 };
        });
    };

    const pickTemplate = (t: Template) => { setSelectedTemplate(t); setPhase('form'); };

    const handleSubmit = async () => {
        if (!clientId || !selectedSlot) return;
        if (!knownContactId) {
            if (!phone.trim()) { setError('Merci de renseigner votre téléphone.'); return; }
            if (!email.trim()) { setError('Merci de renseigner votre email — indispensable pour recevoir la confirmation de votre rendez-vous.'); return; }
            if (contactType === 'PROFESSIONNEL' && !companyName.trim()) { setError('Merci de renseigner le nom de votre société.'); return; }
            if (contactType === 'PRIVÉ' && !lastName.trim()) { setError('Merci de renseigner votre nom.'); return; }
        }
        if (selectedTemplate && selectedTemplate.response_type === 'choice' && selectedChoices.length === 0) { setError('Merci de faire un choix.'); return; }
        const missingRequired = selectedTemplate?.custom_questions.find((q) => {
            if (!q.required) return false;
            if (q.type === 'datetime') { const dt = questionDatetime[q.id]; return !dt?.date_debut || !dt?.heure_debut; }
            return !(questionAnswers[q.id] || '').trim();
        });
        if (missingRequired) { setError(`Merci de répondre à : ${missingRequired.question}`); return; }
        // Une question "Date et heure" partiellement remplie (un seul des deux champs de début) impose de
        // compléter, même si elle n'est pas obligatoire — comme pour tout autre champ de ce type dans l'app.
        const missingQuestionDatetime = selectedTemplate?.custom_questions.find((q) => {
            if (q.type !== 'datetime') return false;
            const dt = questionDatetime[q.id];
            const hasAny = !!(dt?.date_debut || dt?.heure_debut);
            return hasAny && (!dt?.date_debut || !dt?.heure_debut);
        });
        if (missingQuestionDatetime) { setError(`Merci de préciser la date et l'heure pour : ${missingQuestionDatetime.question}`); return; }
        if (isDomicileRdv && !address.trim()) { setError('Merci d\'indiquer votre adresse pour l\'intervention à domicile.'); return; }
        if (isDomicileRdv && !addressSelected) { setError('Merci de sélectionner votre adresse dans la liste proposée.'); return; }
        setSubmitting(true);
        setError('');
        try {
            if (!selectedTemplate) {
                const res: any = await publicRdvProBookingApi.reserve(clientId, {
                    date: selectedDate, start_time: selectedSlot, type: contactType,
                    first_name: firstName.trim(), last_name: lastName.trim(), company_name: companyName.trim(),
                    phone: phone.trim(), email: email.trim(), notes: notes.trim(),
                });
                if (res?.error) { setError(res.error); return; }
                setPhase('success');
                return;
            }

            const startRes: any = await publicRdvProBookingApi.start(clientId, knownContactId
                ? { template_id: selectedTemplate.id, crm_contact_id: knownContactId }
                : {
                    template_id: selectedTemplate.id, type: contactType,
                    first_name: firstName.trim(), last_name: lastName.trim(), company_name: companyName.trim(),
                    phone: phone.trim(), email: email.trim(),
                });
            if (startRes?.error) { setError(startRes.error); return; }
            const token = startRes.token;

            const choicesPayload = selectedChoices.map((c) => ({ id: c.id, qty: selections[c.id] || 1 }));
            const answersPayload = selectedTemplate.custom_questions.map((q) => ({ id: q.id, answer: buildAnswer(q), ...(buildDatetimePayload(q) || {}) })).filter((a) => a.answer.trim());
            const reason = selectedChoices.length > 0
                ? selectedChoices.map((c) => `${c.label}${(selections[c.id] || 1) > 1 ? ` x${selections[c.id]}` : ''}`).join(', ')
                : selectedTemplate.name;
            const rdvPayload = { date: selectedDate, start_time: selectedSlot, subject: reason, notes: notes.trim(), location_type: locationType, address: isDomicileRdv ? address.trim() : undefined, postal_code: isDomicileRdv ? postalCode.trim() : undefined, city: isDomicileRdv ? city.trim() : undefined };
            const respondRes: any = await publicDocumentApi.respond(token, { choices: choicesPayload, text: notes.trim(), custom_answers: answersPayload, rdv: rdvPayload });
            if (respondRes?.error) { setError(respondRes.error); return; }
            // Paiement requis : rien n'est encore enregistré côté serveur (démarche et RDV compris) tant que
            // Stripe ne confirme pas le paiement — inutile d'appeler book-rdv séparément, il est déjà inclus
            // dans ce qui sera créé au retour de paiement (GET .../finalize).
            if (respondRes.pending_payment) {
                window.location.href = respondRes.stripe_payment_link;
                return;
            }

            const bookRes: any = await publicDocumentApi.bookRdv(token, rdvPayload);
            if (bookRes?.error) { setError(bookRes.error); return; }
            setPhase('success');
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la réservation. Merci de réessayer.');
        } finally {
            setSubmitting(false);
        }
    };

    if (phase === 'loading') {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (phase === 'notfound') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4">
                <div className="text-center max-w-sm">
                    <Building2 size={32} className="mx-auto mb-3 text-content-muted opacity-40" />
                    <h1 className="text-base font-bold text-content mb-1.5">Réservation en ligne indisponible</h1>
                    <p className="text-content-muted text-xs">Ce lien de réservation n'est pas valide ou n'est plus actif.</p>
                </div>
                <PoweredByFooter />
            </div>
        );
    }

    if (phase === 'success') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4">
                <div className="text-center max-w-sm bg-surface border border-border rounded-2xl p-7 space-y-3">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
                    <h1 className="text-base font-bold text-content">Demande envoyée</h1>
                    <p className="text-content-muted text-xs leading-relaxed">
                        Votre demande de rendez-vous le <strong className="text-content">{new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> à <strong className="text-content">{selectedSlot}</strong> a bien été envoyée — en attente de confirmation par l'établissement.
                    </p>
                </div>
                <PoweredByFooter />
            </div>
        );
    }

    if (phase === 'type') {
        return (
            <div className="min-h-screen bg-surface-app py-8 px-4">
                <div className="max-w-md mx-auto space-y-5">
                    <header className="text-center space-y-1">
                        {info?.logo_url && <img src={info.logo_url} alt={info.company_name} className="h-10 mx-auto object-contain" />}
                        <h1 className="text-lg font-black text-content">{info?.company_name}</h1>
                        <p className="text-content-muted text-xs">Quel type de rendez-vous souhaitez-vous ?</p>
                    </header>
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-2">
                        {templates.map((t) => (
                            <button key={t.id} type="button" onClick={() => pickTemplate(t)}
                                className="w-full text-left px-4 py-3 rounded-xl border border-border hover:border-brand-500 hover:bg-brand-500/5 transition-all">
                                <p className="text-sm font-bold text-content">{t.name}</p>
                                {t.rdv_duration_minutes && <p className="text-[11px] text-content-muted mt-0.5">Durée prévue : {t.rdv_duration_minutes} minutes</p>}
                            </button>
                        ))}
                    </div>
                    <div className="text-center"><PoweredByFooter /></div>
                </div>
            </div>
        );
    }

    const minDate = todayInput();
    // La durée calculée côté serveur (à partir des choix sélectionnés, disponible une fois qu'une date a été
    // choisie) prime sur la durée fixe du modèle — plus précise dès qu'au moins un choix a sa propre durée.
    const duration = computedDuration ?? selectedTemplate?.rdv_duration_minutes ?? info?.duration_minutes;

    return (
        <div className="min-h-screen bg-surface-app py-8 px-4">
            <div className="max-w-md mx-auto space-y-5">
                <header className="text-center space-y-1">
                    {info?.logo_url && <img src={info.logo_url} alt={info.company_name} className="h-10 mx-auto object-contain" />}
                    <h1 className="text-lg font-black text-content">{info?.company_name}</h1>
                    <p className="text-content-muted text-xs">{selectedTemplate ? selectedTemplate.name : 'Réservez votre rendez-vous en ligne'}</p>
                </header>

                {templates.length > 1 && (
                    <button onClick={() => { setSelectedTemplate(null); setPhase('type'); setSelections({}); setQuestionAnswers({}); }}
                        className="flex items-center gap-1 text-xs font-bold text-content-muted hover:text-content transition-colors">
                        <ChevronLeft size={14} /> Changer de type de rendez-vous
                    </button>
                )}

                {selectedTemplate?.instructions_text && (
                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <p className="text-sm text-content whitespace-pre-wrap">{selectedTemplate.instructions_text}</p>
                    </div>
                )}

                <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
                    {selectedTemplate && (selectedTemplate.response_type === 'choice' || selectedTemplate.response_type === 'both') && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">
                                Votre choix {selectedTemplate.allow_multiple_choices ? '(plusieurs possibles)' : ''}
                            </label>
                            {selectedTemplate.choices.map((choice) => {
                                const isSelected = choice.id in selections;
                                return (
                                    <button key={choice.id} type="button" onClick={() => toggleChoice(choice)}
                                        className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2.5 rounded-xl border text-sm font-bold transition-all ${isSelected ? 'bg-brand-500/10 border-brand-500 text-brand-600' : 'border-border text-content hover:bg-surface-soft'}`}>
                                        <span>{choice.label}</span>
                                        {choice.price_ttc > 0 && <span className="text-xs font-normal shrink-0">{formatPrice(choice.price_ttc)}</span>}
                                    </button>
                                );
                            })}
                            {kmFee && kmFee.fee > 0 && (
                                <p className="text-right text-xs text-content-muted">Frais kilométriques ({kmFee.distance_km} km aller-retour) : {formatPrice(kmFee.fee)}</p>
                            )}
                            {orderTotal > 0 && (
                                <p className="text-right text-sm font-bold text-content pt-1">Total : {formatPrice(orderTotal + (kmFee?.fee || 0))}</p>
                            )}
                        </div>
                    )}

                    {selectedTemplate && selectedTemplate.custom_questions.length > 0 && (
                        <div className="space-y-3">
                            {selectedTemplate.custom_questions.map((q) => {
                                const selectedLabels = getSelectedLabels(questionAnswers[q.id]);
                                const toggleOption = (label: string) => setQuestionAnswers((prev) => {
                                    const current = getSelectedLabels(prev[q.id]);
                                    let next: string[];
                                    if (q.allow_multiple) next = current.includes(label) ? current.filter((l) => l !== label) : [...current, label];
                                    else next = current.includes(label) ? [] : [label];
                                    return { ...prev, [q.id]: next.join(', ') };
                                });
                                return (
                                    <div key={q.id} className="space-y-1.5">
                                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                                            {q.question}{q.required && <span className="text-red-500"> *</span>}
                                            {(q.help_text || q.help_image) && (
                                                <button type="button" onClick={() => toggleQuestionHelp(q.id)}
                                                    className="w-4 h-4 rounded-full bg-surface-soft border border-border text-content-muted hover:text-brand-600 hover:border-brand-500 flex items-center justify-center normal-case tracking-normal shrink-0" title="Aide">
                                                    <HelpCircle size={11} />
                                                </button>
                                            )}
                                        </label>
                                        {(q.help_text || q.help_image) && openHelpIds.has(q.id) && (
                                            <div className="bg-surface-soft border border-border rounded-xl p-3 space-y-2">
                                                {q.help_image && <img src={q.help_image} alt="Aide" className="max-h-48 w-auto rounded-lg mx-auto" />}
                                                {q.help_text && <p className="text-xs text-content whitespace-pre-wrap">{q.help_text}</p>}
                                            </div>
                                        )}
                                        {q.type === 'select' ? (
                                            <>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {q.options.map((o) => (
                                                        <button key={o.id} type="button" onClick={() => toggleOption(o.label)}
                                                            className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${selectedLabels.includes(o.label) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                            {o.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {q.options.filter((o) => selectedLabels.includes(o.label) && o.message).map((o) => (
                                                    <p key={o.id} className="text-xs text-content bg-surface-soft rounded-xl p-3 border border-border">{o.message}</p>
                                                ))}
                                                {q.options.filter((o) => selectedLabels.includes(o.label) && o.has_detail_field).map((o) => (
                                                    <input key={o.id} type="text" value={optionDetails[o.id] || ''}
                                                        onChange={(e) => setOptionDetails((prev) => ({ ...prev, [o.id]: e.target.value }))}
                                                        placeholder="Précisez..." className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                                ))}
                                            </>
                                        ) : q.type === 'datetime' ? (
                                            (() => {
                                                const dt = questionDatetime[q.id] || { date_debut: '', heure_debut: '', date_fin: '', heure_fin: '' };
                                                const setDt = (patch: Partial<typeof dt>) => setQuestionDatetime((prev) => ({ ...prev, [q.id]: { ...dt, ...patch } }));
                                                return (
                                                    <div className="grid grid-cols-2 gap-2 bg-surface-soft border border-border rounded-xl p-3">
                                                        <div className="space-y-1 col-span-2 sm:col-span-1">
                                                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date *</label>
                                                            <input type="date" value={dt.date_debut} onChange={(e) => setDt({ date_debut: e.target.value })}
                                                                className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                        </div>
                                                        <div className="space-y-1 col-span-2 sm:col-span-1">
                                                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure *</label>
                                                            <input type="time" value={dt.heure_debut} onChange={(e) => setDt({ heure_debut: e.target.value })}
                                                                className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                        </div>
                                                        {q.allow_end_date !== false && (
                                                            <>
                                                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date de fin</label>
                                                                    <input type="date" value={dt.date_fin} onChange={(e) => setDt({ date_fin: e.target.value })}
                                                                        className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                </div>
                                                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure de fin</label>
                                                                    <input type="time" value={dt.heure_fin} onChange={(e) => setDt({ heure_fin: e.target.value })}
                                                                        className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <input type="text" value={questionAnswers[q.id] || ''} onChange={(e) => setQuestionAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!isBlocked && info?.location_mode === 'les_deux' && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Où souhaitez-vous être reçu(e) ?</label>
                            <div className="grid grid-cols-2 gap-2">
                                {(['domicile', 'entreprise'] as const).map((t) => (
                                    <button key={t} type="button" onClick={() => setLocationType(t)}
                                        className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationType === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {t === 'domicile' ? 'À mon adresse' : "Dans l'établissement"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isBlocked && isDomicileRdv && (
                        <div className="space-y-2">
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Adresse</label>
                                <AddressAutocompleteInput value={address} onChange={(v) => { setAddress(v); setAddressSelected(false); }}
                                    onSelect={(s) => { setPostalCode(s.postcode); setCity(s.city); setAddressSelected(true); }}
                                    placeholder="Numéro et rue"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                {address.trim() && !addressSelected && (
                                    <p className="text-[11px] text-amber-500 font-semibold">Merci de sélectionner votre adresse dans la liste proposée.</p>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Code postal</label>
                                    <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Ville</label>
                                    <input value={city} onChange={(e) => setCity(e.target.value)}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            </div>
                        </div>
                    )}

                    {!isBlocked && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><CalendarClock size={12} /> Date et créneau</label>
                            {duration && <p className="text-[11px] text-content-muted">Durée prévue : {duration} minutes</p>}
                            <input type="date" min={minDate} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            {loadingSlots && <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-content-muted" /></div>}
                            {!loadingSlots && selectedDate && slots.length === 0 && <p className="text-xs text-content-muted">Aucun créneau disponible ce jour-là.</p>}
                            {slots.length > 0 && (
                                <div className="grid grid-cols-4 gap-2">
                                    {slots.map((slot) => (
                                        <button key={slot} type="button" onClick={() => setSelectedSlot(slot)}
                                            className={`px-2 py-2 rounded-lg border text-xs font-bold transition-all ${selectedSlot === slot ? 'bg-brand-500/10 border-brand-500 text-brand-600' : 'border-border text-content hover:bg-brand-500/10 hover:border-brand-500 hover:text-brand-600'}`}>
                                            {slot}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!isBlocked && selectedSlot && (
                        <>
                            <div className="space-y-1.5">
                                {!knownContactId && (
                                    <>
                                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Vos informations</label>
                                        {!info?.professionals_only && (
                                            <div className="grid grid-cols-2 gap-2">
                                                {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                                                    <button key={t} type="button" onClick={() => setContactType(t)}
                                                        className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${contactType === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {contactType === 'PROFESSIONNEL' ? (
                                            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder="Nom de la société" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                                                    placeholder="Prénom" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                                                    placeholder="Nom" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                            </div>
                                        )}
                                        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                            placeholder="Téléphone" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Email" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                    </>
                                )}
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                                    placeholder="Message (optionnel)" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                            </div>

                            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

                            <button onClick={handleSubmit} disabled={submitting}
                                className="w-full px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all">
                                {submitting ? 'Envoi...' : 'Envoyer ma demande'}
                            </button>
                        </>
                    )}
                </div>

                <div className="text-center"><PoweredByFooter /></div>
            </div>
        </div>
    );
};

export default RdvProBookingWidget;
